import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MainBrandModal from "../MainBrandModal/MainBrandModal";
import ConfigAndMapDetailsModal from "../ConfigAndMapDetailsModal/ConfigAndMapDetailsModal";
import FamilyDrawer from "../FamilyDrawer/FamilyDrawer";
import EditFamilyModal from "../../../NeedAttention/NeedAttentionTabs/EditFamilyModal";
import type { FamilyItem } from "../../../NeedAttention/NeedAttentionTabs/NeedAttentionFamiliesAction";
import { selectUserToken, selectUserData } from "../../../../../../features/userSlice";
import { familiesEndPointLogic } from "../../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import { CheckPermissions } from "../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useWebsiteSharedUid } from "../../../../../../customHooks/useWebsiteSharedUid";
import type { CoverageRow } from "../../../shared/candidateCatalogue";

type StepKey = "fetcher" | "unifier" | "advisor";

const STEP_DEFS: { key: StepKey; title: string; color: string }[] = [
  { key: "fetcher", title: "Fetcher", color: "#607D8B" },
  { key: "unifier", title: "Unifier", color: "#E91E63" },
  { key: "advisor", title: "Advisor", color: "#03A9F4" },
];

// Action-tracking ids, keyed by step.
const CREATE_FAMILY_TRACKER: Record<StepKey, string> = {
  fetcher: "984",
  unifier: "985",
  advisor: "986",
};
const EDIT_FAMILY_TRACKER: Record<StepKey, string> = {
  fetcher: "987",
  unifier: "988",
  advisor: "989",
};
const DUPLICATE_FAMILY_TRACKER: Record<StepKey, string> = {
  fetcher: "990",
  unifier: "991",
  advisor: "992",
};
const VIEW_BRANDS_TRACKER: Record<StepKey, string> = {
  fetcher: "1011",
  unifier: "1012",
  advisor: "1013",
};
const CONFIG_DETAILS_TRACKER: Record<StepKey, string> = {
  fetcher: "1014",
  unifier: "1015",
  advisor: "1016",
};

interface Props {
  interfaceDetail: any;
  interfaceName: string;
  brandStepCoverage: any[];
  interfaceId: string | number | null;
  coverageRow?: CoverageRow | null;
}

function recipeLine(thing: any) {
  return [
    thing.report_name,
    thing.collection_method,
    thing.collection_type,
    thing.format,
    thing.sub_filtering_method,
  ]
    .filter(Boolean)
    .join(" - ");
}

function FamilyCard({
  family,
  stepKey,
  stepTitle,
  interfaceName,
  brandStepCoverage,
  onOpenDrawer,
  onRefetch,
  hasPermission,
}: {
  family: any;
  stepKey: StepKey;
  stepTitle: string;
  interfaceName: string;
  brandStepCoverage: any[];
  onOpenDrawer: (title: string, type: string, sourceFamily?: any) => void;
  onRefetch: () => void;
  hasPermission: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const thingsLabel = stepKey === "fetcher" ? "configs" : "maps";
  const things = family.things ?? [];
  // Memoized — `family.brands ?? []` would otherwise hand back a fresh empty
  // array on every render whenever brands is falsy, silently defeating the
  // brandsWithStatusRows memoization below (it depends on `brands` by
  // reference).
  const brands = React.useMemo(() => family.brands ?? [], [family.brands]);

  // The action icons (Edit/Duplicate/chevron) live inside this header row,
  // which also toggles the accordion on click. Guarding on `defaultPrevented`
  // (set by the actions Box below) — rather than stopPropagation — keeps the
  // click from also toggling the accordion without blocking native bubbling,
  // since the app's global click-tracking listener is attached on `document`
  // and stopPropagation would prevent it from ever seeing these clicks.
  const handleToggle = (e?: React.SyntheticEvent) => {
    if (e?.defaultPrevented) return;
    setExpanded((prev) => !prev);
  };

  const [selectedThing, setSelectedThing] = React.useState<any>(null);
  const [openConfigAndMapDetailsModal, setOpenConfigAndMapDetailsModal] =
    React.useState(false);
  const [openBrandsModal, setOpenBrandsModal] = React.useState(false);

  const handleOpenConfigModal = (thing: any) => {
    setSelectedThing(thing);
    setOpenConfigAndMapDetailsModal(true);
  };
  const handleCloseConfigModal = () => {
    setOpenConfigAndMapDetailsModal(false);
  };

  const handleOpenBrandsModal = () => {
    setOpenBrandsModal(true);
  };
  const handleCloseBrandsModal = () => {
    setOpenBrandsModal(false);
  };

  // Memoized so its reference is stable across incidental re-renders (e.g. an
  // unrelated loading-flag toggle elsewhere in the `families` slice) — the
  // Edit drawer's own Q1 fetch keys off this object by reference, and would
  // otherwise re-fire every time this object was recreated while open.
  const familyItem: FamilyItem = React.useMemo(
    () => ({
      id: family.family_id,
      name: family.ui_family_name,
      count: family.family_id,
      provider: interfaceName,
      configs: family.things_count,
      brands: family.brands_count,
      backendInterfaceId: family.backend_interface_id,
      step: stepKey,
    }),
    [family, interfaceName, stepKey],
  );
  const [openEditFamilyModal, setOpenEditFamilyModal] = React.useState(false);
  const handleOpenEditFamilyModal = () => setOpenEditFamilyModal(true);
  const handleCloseEditFamilyModal = () => setOpenEditFamilyModal(false);

  // All brands connected to this family, with their active/inactive status.
  // brandStepCoverage is checked first (matches this step's family), falling
  // back to the brand's own is_active flag (used for the chip list below) so
  // every connected brand shows up here, not just the ones with coverage rows.
  const brandsWithStatusRows = React.useMemo(() => {
    const activeStatusByBrandId = new Map<number | string, boolean>();
    brandStepCoverage
      .filter((row: any) => row[`${stepKey}_family_id`] === family.family_id)
      .forEach((row: any) =>
        activeStatusByBrandId.set(row.brand_id, row.is_active),
      );

    return brands.map((brand: any) => ({
      brand_id: brand.brand_id,
      brand_name: brand.brand_name,
      status: activeStatusByBrandId.get(brand.brand_id) ?? brand.is_active ?? false,
    }));
  }, [brands, brandStepCoverage, stepKey, family.family_id]);

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #0000001F",
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {/* Family Header */}
      <Box
        onClick={handleToggle}
        sx={{
          minHeight: 60,
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: expanded ? "1px solid #e5e5e5" : "none",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            {family.ui_family_name} ({family.family_id})
            {family.brands_count === 0 && (
              <Box
                component="span"
                sx={{
                  backgroundColor: "#F0F0F3",
                  color: "#00000099",
                  fontSize: 11,
                  borderRadius: "5px",
                  px: 0.8,
                  py: 0.1,
                }}
              >
                empty
              </Box>
            )}
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: "#00000099",
            }}
          >
            {family.things_count} {thingsLabel} - {family.brands_count} brands
          </Typography>
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
          onClick={(e) => e.preventDefault()}
        >
          <IconButton
            data-tracker={EDIT_FAMILY_TRACKER[stepKey]}
            onClick={handleOpenEditFamilyModal}
            disabled={!hasPermission}
            title={hasPermission ? undefined : "You don't have permission to edit families."}
            size="small"
            sx={{
              color: "#9c27b0",
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            data-tracker={DUPLICATE_FAMILY_TRACKER[stepKey]}
            onClick={() => onOpenDrawer(stepTitle, thingsLabel, family)}
            disabled={!hasPermission}
            title={hasPermission ? undefined : "You don't have permission to duplicate families."}
            size="small"
            sx={{
              color: "#9c27b0",
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>

          {/* Accordion button */}
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              color: "#777",
            }}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Accordion Content */}
      {expanded && (
        <Box
          sx={{
            p: 1,
          }}
        >
          {/* Configs / Maps */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.8,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: "#00000099",
                textTransform: "uppercase",
              }}
            >
              {thingsLabel}
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                color: "#00000099",
              }}
            >
              {family.things_count}
            </Typography>
          </Box>

          {/* Config / Map Items */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {things.map((thing: any) => (
              <Box
                key={thing.config_id ?? thing.map_id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  p: 1,
                  minHeight: 56,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    data-tracker={CONFIG_DETAILS_TRACKER[stepKey]}
                    onClick={() => handleOpenConfigModal(thing)}
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "#9C27B0",
                      lineHeight: 1.4,
                      cursor: "pointer",
                    }}
                  >
                    {thing.configuration_name ?? thing.map_name}{" "}
                    <Box
                      component="span"
                      sx={{
                        color: "#00000099",
                        fontWeight: 500,
                        fontSize: 14,
                      }}
                    >
                      ({thing.config_id ?? thing.map_id})
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#00000099",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {thing.destination_table ?? recipeLine(thing)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {selectedThing && (
            <ConfigAndMapDetailsModal
              open={openConfigAndMapDetailsModal}
              handleClose={handleCloseConfigModal}
              stepKey={stepKey}
              interfaceName={interfaceName}
              thing={selectedThing}
            />
          )}

          {/* Brands */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: "#00000099",
                textTransform: "uppercase",
              }}
            >
              BRANDS
            </Typography>

            <Typography
              data-tracker={VIEW_BRANDS_TRACKER[stepKey]}
              onClick={family.brands_count > 0 ? handleOpenBrandsModal : undefined}
              sx={{
                color: family.brands_count > 0 ? "#9c27b0" : "#00000099",
                fontSize: 12,
                fontWeight: 500,
                textDecoration: family.brands_count > 0 ? "underline" : "none",
                cursor: family.brands_count > 0 ? "pointer" : "default",
              }}
            >
              {family.brands_count}
            </Typography>
            <MainBrandModal
              open={openBrandsModal}
              handleClose={handleCloseBrandsModal}
              familyName={family.ui_family_name}
              interfaceName={interfaceName}
              rows={brandsWithStatusRows}
            />
          </Box>

          {/* Brand Chips */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
            }}
          >
            {brands.map((brand: any) => (
              <Chip
                key={brand.brand_id}
                label={brand.brand_name}
                variant="outlined"
                sx={{
                  height: 33,
                  borderRadius: "16px",
                  borderColor: "#0000001F",
                  borderStyle: brand.is_active ? "solid" : "dashed",
                  color: brand.is_active ? "#000000DE" : "#00000061",
                  backgroundColor: "#fff",
                  fontSize: 14,

                  "& .MuiChip-label": {
                    px: 1.2,
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      <EditFamilyModal
        open={openEditFamilyModal}
        handleClose={handleCloseEditFamilyModal}
        item={familyItem}
        onSaved={onRefetch}
      />
    </Card>
  );
}

export default function CreateNewFamily({
  interfaceDetail,
  interfaceName: interfaceNameProp,
  brandStepCoverage,
  interfaceId,
  coverageRow,
}: Props) {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const userData = useSelector(selectUserData);
  // The EP treats website_shared_uid as a required dummy input — pass the
  // first website_shared_uid the user can access from /me, same as the
  // initial Q1 fetch in Interface.tsx.
  const website_shared_uid = useWebsiteSharedUid();
  const hasCreateEditDeleteFamiliesPermission = CheckPermissions(
    userData?.data?.products?.data_collection,
    "businesse_advisor",
    "create_edit_delete_families",
  );
  const [open, setOpen] = React.useState(false);
  const [selectedFamilyTitle, setSelectedFamilyTitle] = React.useState("");
  const [selectedFamilyType, setSelectedFamilyType] = React.useState("");
  const [selectedStepKey, setSelectedStepKey] = React.useState<StepKey>("fetcher");
  const [sourceFamily, setSourceFamily] = React.useState<any>(null);

  const refetch = () => {
    familiesEndPointLogic.getFamiliesOverview(
      oauthTokenHeader,
      website_shared_uid,
      interfaceId,
      dispatch,
    );
  };

  const toggleDrawer = (title: string, type: string, source?: any) => {
    const stepKey = (STEP_DEFS.find((s) => s.title === title)?.key ?? "fetcher") as StepKey;
    setSelectedFamilyTitle(title);
    setSelectedFamilyType(type);
    setSelectedStepKey(stepKey);
    setSourceFamily(source ?? null);
    setOpen(true);
  };

  const toggleDrawerClose = () => {
    setOpen(false);
    setSelectedFamilyTitle("");
    setSelectedFamilyType("");
    setSourceFamily(null);
  };

  const interfaceName = interfaceNameProp ?? "N/A";

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={2}>
        {STEP_DEFS.map((step) => {
          const families = interfaceDetail?.steps?.[step.key]?.families ?? [];

          return (
            <Grid item xs={12} md={4} key={step.key}>
              {/* Top colored border */}
              <Box
                sx={{
                  borderTop: `4px solid ${step.color}`,
                  borderRadius: families.length === 0 ? "5px" : "5px 5px 0 0",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              >
                {/* Column Header */}
                <Box
                  sx={{
                    px: 1,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom:
                      families.length === 0 ? "none" : "1px solid #e5e5e5",
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 16,
                        fontWeight: 500,
                      }}
                    >
                      {step.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#00000099",
                      }}
                    >
                      {families.length} {families.length === 1 ? "family" : "families"}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined"
                    endIcon={<AddIcon />}
                    data-tracker={CREATE_FAMILY_TRACKER[step.key]}
                    onClick={() =>
                      toggleDrawer(
                        step.title,
                        step.key === "fetcher" ? "configs" : "maps",
                      )
                    }
                    disabled={!hasCreateEditDeleteFamiliesPermission}
                    title={
                      hasCreateEditDeleteFamiliesPermission
                        ? undefined
                        : "You don't have permission to create families."
                    }
                    sx={{
                      color: "#9C27B0",
                      borderColor: "#9C27B0",
                      fontWeight: 500,
                      fontSize: 13,
                      height: 36,
                      px: 1.4,
                      whiteSpace: "nowrap",

                      "&:hover": {
                        borderColor: "#9C27B0",
                        backgroundColor: "#faf0fc",
                      },
                    }}
                  >
                    Create New Family
                  </Button>

                  {/* Empty message */}
                  {families.length === 0 && (
                    <Box
                      width={"100%"}
                      sx={{
                        borderRadius: "4px",
                        border: "1px dashed #BDBDBD",
                        p: 1,
                        mt: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "#00000099",
                          textAlign: "center",
                        }}
                      >
                        No {step.title} Families
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Family Cards */}
                {families.length > 0 && (
                  <Box
                    sx={{
                      p: 1,
                      background: "#FAFAFA",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {families.map((family: any) => (
                      <FamilyCard
                        key={family.family_id}
                        family={family}
                        stepKey={step.key}
                        stepTitle={step.title}
                        interfaceName={interfaceName}
                        brandStepCoverage={brandStepCoverage}
                        onOpenDrawer={toggleDrawer}
                        onRefetch={refetch}
                        hasPermission={hasCreateEditDeleteFamiliesPermission}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          );
        })}
        <FamilyDrawer
          open={open}
          toggleDrawerClose={toggleDrawerClose}
          familiesTitle={selectedFamilyTitle}
          type={selectedFamilyType}
          interfaceName={interfaceName}
          stepKey={selectedStepKey}
          backendInterfaceId={interfaceDetail?.backend_interface_id ?? interfaceId}
          interfaceDetail={interfaceDetail}
          coverageRow={coverageRow}
          sourceFamily={sourceFamily}
          onCreated={refetch}
        />
      </Grid>
    </Box>
  );
}
